import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, API, BACKEND_URL } from "../../lib/api";
import { toast } from "sonner";
import { Copy, Share2, LogOut, Send, Link2, KeyRound, MessageCircle, Loader2, Clock, Bell, Camera, ImagePlus, User, Megaphone, Wallet, Download, XCircle, Trophy } from "lucide-react";
import { inscreverPush } from "../../lib/push";
import { processarFoto, uploadFoto } from "../../lib/image";

const inputCls =
  "w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#F5B301] placeholder:text-slate-400";

const brl = (v) => (v == null ? "—" : `R$ ${Number(v).toFixed(2).replace(".", ",")}`);
const fmtDt = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");
const fmtD = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join("/") : "—");
const fotoUrl = (idPublico, v) => `${API}/indicador-foto/${idPublico}${v ? `?v=${v}` : ""}`;
const imgUrl = (path) => `${BACKEND_URL}${path}`;

const PIX_STATUS_STYLE = {
  nao_cadastrados: "bg-slate-100 text-slate-500",
  pendente_conferencia: "bg-amber-100 text-amber-700",
  aprovados: "bg-emerald-100 text-emerald-700",
  necessita_correcao: "bg-red-100 text-red-600",
};
const COM_STYLE = {
  aguardando_conclusao: "bg-slate-100 text-slate-600",
  liberada: "bg-blue-100 text-blue-700",
  paga: "bg-emerald-100 text-emerald-700",
  cancelada: "bg-red-100 text-red-600",
};
const PIX_TIPOS = [
  { v: "cpf", l: "CPF" }, { v: "cnpj", l: "CNPJ" }, { v: "email", l: "E-mail" },
  { v: "telefone", l: "Telefone" }, { v: "aleatoria", l: "Chave aleatória" },
];

const TABS = [
  { v: "inicio", l: "INÍCIO", icon: User },
  { v: "ranking", l: "RANKING", icon: Trophy },
  { v: "divulgacao", l: "DIVULGAÇÃO", icon: Megaphone },
  { v: "carteira", l: "CARTEIRA", icon: Wallet },
  { v: "suporte", l: "SUPORTE", icon: MessageCircle },
];

export default function ClubePainel() {
  const navigate = useNavigate();
  const [ind, setInd] = useState(null);
  const [link, setLink] = useState(null);
  const [carteira, setCarteira] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [msgTexto, setMsgTexto] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [pixForm, setPixForm] = useState({ tipo: "cpf", chave: "", titular_nome: "", titular_doc: "", confirmacao: false });
  const [pixSalvando, setPixSalvando] = useState(false);
  const [tab, setTab] = useState("inicio");
  const [fotoV, setFotoV] = useState("");
  const [fotoPendente, setFotoPendente] = useState(null);
  const [fotoSalvando, setFotoSalvando] = useState(false);
  const cameraRef = useRef(null);
  const galeriaRef = useRef(null);
  const [divulgacao, setDivulgacao] = useState(null);
  const [shareImg, setShareImg] = useState(null);
  const [shareTexto, setShareTexto] = useState("");
  const [shareBusy, setShareBusy] = useState(false);
  const [ranking, setRanking] = useState(null);
  const [rankingVisao, setRankingVisao] = useState("mes");

  const auth = useCallback(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem("ind_token") || ""}` } }), []);

  const logout = useCallback(async () => {
    try { await api.post("/indicador/logout", {}, auth()); } catch {}
    localStorage.removeItem("ind_token");
    navigate("/clube/login");
  }, [auth, navigate]);

  const loadMsgs = useCallback(() => {
    api.get("/indicador/suporte", auth()).then((r) => setMsgs(r.data.mensagens || [])).catch(() => {});
  }, [auth]);

  const loadDivulgacao = useCallback(() => {
    api.get("/indicador/divulgacao", auth()).then((r) => setDivulgacao(r.data)).catch(() => setDivulgacao({ imagens: [] }));
  }, [auth]);

  const loadRanking = useCallback(() => {
    api.get("/indicador/ranking", auth()).then((r) => setRanking(r.data)).catch(() => setRanking({ ranking_geral: [], ranking_mes: [] }));
  }, [auth]);

  useEffect(() => {
    if (!localStorage.getItem("ind_token")) return navigate("/clube/login");
    api.get("/indicador/me", auth())
      .then((r) => {
        setInd(r.data);
        if (r.data.status === "ativo") {
          api.get("/indicador/link", auth()).then((x) => setLink(x.data)).catch(() => {});
          api.get("/indicador/carteira", auth()).then((x) => setCarteira(x.data)).catch(() => {});
          loadMsgs();
        }
        const pix = r.data.pix || {};
        if (pix.chave) setPixForm({ tipo: pix.tipo || "cpf", chave: pix.chave, titular_nome: pix.titular_nome || "", titular_doc: pix.titular_doc || "", confirmacao: false });
      })
      .catch(() => { localStorage.removeItem("ind_token"); navigate("/clube/login"); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (ind?.status !== "ativo") return undefined;
    const i = setInterval(loadMsgs, 15000);
    return () => clearInterval(i);
  }, [ind, loadMsgs]);

  useEffect(() => {
    if (tab === "divulgacao" && ind?.status === "ativo" && !divulgacao) loadDivulgacao();
  }, [tab, ind, divulgacao, loadDivulgacao]);

  useEffect(() => {
    if (tab === "ranking" && ind?.status === "ativo" && !ranking) loadRanking();
  }, [tab, ind, ranking, loadRanking]);

  if (!ind) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#F5B301] animate-spin" />
      </div>
    );
  }

  if (ind.status !== "ativo") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6" data-testid="clube-painel-bloqueado">
        <div className="w-full max-w-sm bg-white rounded-2xl border border-slate-100 shadow-sm p-6 text-center">
          <Clock className="w-9 h-9 text-[#F5B301] mx-auto mb-3" />
          <h1 className="font-heading text-lg font-bold text-navy">{ind.status_label}</h1>
          <p className="text-xs text-slate-500 mt-2 leading-relaxed" data-testid="clube-status-msg">
            {ind.status === "aguardando_aprovacao"
              ? "Seu cadastro está em análise pela administração. Você receberá um e-mail quando for aprovado."
              : "Seu acesso não está liberado no momento. Em caso de dúvida, fale com a administração."}
          </p>
          <button onClick={logout} data-testid="clube-bloqueado-logout" className="mt-5 w-full py-3 rounded-xl bg-navy text-white font-extrabold text-xs active:scale-95 transition-transform">
            SAIR
          </button>
        </div>
      </div>
    );
  }

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(link?.link || "");
      toast.success("Link copiado!");
    } catch {
      toast.error("Não foi possível copiar. Selecione e copie manualmente.");
    }
  };

  const compartilhar = async () => {
    const texto = `Faça seu orçamento de frete ou mudança com a Carlos Júnior Fretes e Mudanças pelo meu link: ${link?.link}`;
    if (navigator.share) {
      try { await navigator.share({ title: "Carlos Jr Fretes e Mudanças", text: texto, url: link?.link }); } catch {}
    } else {
      copiar();
    }
  };

  const copiarCodigo = async () => {
    try {
      await navigator.clipboard.writeText(link?.codigo || ind.id_publico);
      toast.success("Código copiado!");
    } catch {
      toast.error("Não foi possível copiar. Selecione e copie manualmente.");
    }
  };

  const compartilharCodigo = async () => {
    const codigo = link?.codigo || ind.id_publico;
    const texto = `Use meu código de indicação ${codigo} ao fazer seu orçamento de frete ou mudança na Carlos Júnior Fretes e Mudanças: ${link?.link}`;
    if (navigator.share) {
      try { await navigator.share({ title: "Carlos Jr Fretes e Mudanças", text: texto }); } catch {}
    } else {
      copiarCodigo();
    }
  };

  const ativarNotificacoes = async () => {
    const ok = await inscreverPush("/push/subscribe/indicador");
    if (ok) toast.success("Notificações ativadas neste aparelho!");
    else toast.error("Não foi possível ativar. Verifique a permissão de notificações do navegador.");
  };

  const escolherFoto = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!(f.type || "").startsWith("image/")) return toast.error("Envie um arquivo de imagem (JPG, PNG ou WebP)");
    if (f.size > 10 * 1024 * 1024) return toast.error("Imagem muito grande (máx. 10 MB)");
    try {
      const otimizada = await processarFoto(f);
      if (fotoPendente?.preview) URL.revokeObjectURL(fotoPendente.preview);
      setFotoPendente({ file: otimizada, preview: URL.createObjectURL(otimizada) });
    } catch {
      toast.error("Não foi possível ler a foto. Tente outra imagem.");
    }
  };

  const cancelarFoto = () => {
    if (fotoPendente?.preview) URL.revokeObjectURL(fotoPendente.preview);
    setFotoPendente(null);
  };

  const confirmarFoto = async () => {
    if (!fotoPendente || fotoSalvando) return;
    setFotoSalvando(true);
    try {
      const path = await uploadFoto(fotoPendente.file, "indicador_foto");
      await api.put("/indicador/foto", { foto: path }, auth());
      setInd((i) => ({ ...i, foto: path }));
      setFotoV(Date.now());
      URL.revokeObjectURL(fotoPendente.preview);
      setFotoPendente(null);
      toast.success("Foto de perfil atualizada com sucesso!");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Não foi possível salvar a foto. Tente novamente.");
    } finally {
      setFotoSalvando(false);
    }
  };

  const abrirShare = (img) => {
    setShareImg(img);
    setShareTexto(divulgacao?.texto || "");
  };

  const linkIndicacao = divulgacao?.link || link?.link || "";
  const textoCompartilhamento = () => `${(shareTexto || "").trim()}\n\nAcesse pelo meu link de indicação: ${linkIndicacao}`;

  const copiarTextoLink = async () => {
    try {
      await navigator.clipboard.writeText(textoCompartilhamento());
      toast.success("Texto com seu link de indicação copiado!");
    } catch {
      toast.error("Não foi possível copiar. Selecione e copie manualmente.");
    }
  };

  const baixarImagem = () => {
    if (!shareImg) return;
    const a = document.createElement("a");
    a.href = imgUrl(shareImg.url);
    a.download = `carlosjr-divulgacao-${shareImg.imagem_id}.jpg`;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const compartilharImagem = async () => {
    if (!shareImg || shareBusy) return;
    if (!linkIndicacao) {
      toast.error("Seu link de indicação ainda não está disponível. Tente novamente em instantes.");
      return;
    }
    const texto = textoCompartilhamento();
    setShareBusy(true);
    try {
      const resp = await fetch(imgUrl(shareImg.url));
      const blob = await resp.blob();
      const file = new File([blob], "carlosjr-divulgacao.jpg", { type: blob.type || "image/jpeg" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: "Carlos Jr Fretes e Mudanças", text: texto });
          toast.success("Divulgação preparada com a imagem e o seu link de indicação!");
          setShareBusy(false);
          return;
        } catch (e) {
          if (e?.name === "AbortError") { setShareBusy(false); return; }
        }
      }
    } catch {}
    try { await navigator.clipboard.writeText(texto); } catch {}
    baixarImagem();
    toast.info("Este aparelho não permite enviar imagem e link juntos. Copiamos o texto com seu link e baixamos a imagem — cole o texto na legenda ao publicar.", { duration: 7000 });
    setShareBusy(false);
  };

  const salvarPix = async (e) => {
    e.preventDefault();
    if (pixSalvando) return;
    if (!pixForm.confirmacao) return toast.error("Confirme que os dados de recebimento estão corretos");
    setPixSalvando(true);
    try {
      const r = await api.put("/indicador/pix", pixForm, auth());
      setInd((i) => ({ ...i, pix: r.data.pix }));
      toast.success("Dados Pix salvos! Aguardando conferência da administração.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível salvar os dados Pix.");
    } finally {
      setPixSalvando(false);
    }
  };

  const enviarMsg = async (e) => {
    e.preventDefault();
    if (!msgTexto.trim() || enviando) return;
    setEnviando(true);
    try {
      await api.post("/indicador/suporte", { texto: msgTexto.trim() }, auth());
      setMsgTexto("");
      loadMsgs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível enviar a mensagem.");
    } finally {
      setEnviando(false);
    }
  };

  const resumo = carteira?.resumo || {};

  return (
    <div className="min-h-screen bg-[#0b1220] flex justify-center">
      <div className="w-full max-w-md mx-auto min-h-screen bg-slate-50 shadow-2xl pb-10" data-testid="clube-painel">
        <div className="bg-navy px-4 pt-5 pb-6 rounded-b-3xl">
          <div className="flex items-center gap-3">
            {ind.foto ? (
              <img src={fotoUrl(ind.id_publico, fotoV)} alt="Foto de perfil" className="w-12 h-12 rounded-full object-cover border-2 border-[#F5B301]" data-testid="indicador-foto"
                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/logo.jpg"; e.currentTarget.classList.remove("object-cover"); e.currentTarget.classList.add("object-contain", "bg-white", "p-1"); }} />
            ) : (
              <div className="w-12 h-12 rounded-full bg-[#F5B301] flex items-center justify-center text-navy font-extrabold">{ind.nome?.[0]}</div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-white font-heading font-bold text-sm truncate" data-testid="indicador-nome">{ind.nome}</p>
              <span className="inline-block text-[10px] font-extrabold bg-[#F5B301] text-navy px-2 py-0.5 rounded-full" data-testid="indicador-id">{ind.id_publico}</span>
            </div>
            <button onClick={logout} data-testid="indicador-logout-btn" className="p-2 text-slate-300 hover:text-red-400" aria-label="Sair">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="px-4 -mt-4">
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-1.5 grid grid-cols-5 gap-1" data-testid="clube-tabs">
            {TABS.map((t) => (
              <button key={t.v} onClick={() => setTab(t.v)} data-testid={`clube-tab-${t.v}`}
                className={`py-2 rounded-xl text-[8px] font-extrabold flex flex-col items-center justify-center gap-1 active:scale-95 transition-transform ${tab === t.v ? "bg-navy text-[#F5B301]" : "text-slate-500"}`}>
                <t.icon className="w-4 h-4" /> {t.l}
              </button>
            ))}
          </div>
        </div>

        <div className="px-4 mt-4 space-y-4">
          {tab === "inicio" && (<>
            <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid="perfil-section">
              <h2 className="text-xs font-bold tracking-wider uppercase text-slate-400 mb-2 flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> Meu perfil</h2>
              <div className="space-y-1.5 text-xs text-slate-600">
                <p data-testid="perfil-nome"><strong className="text-navy">Nome:</strong> {ind.nome}</p>
                <p data-testid="perfil-email"><strong className="text-navy">E-mail:</strong> {ind.email}</p>
                <p data-testid="perfil-telefone"><strong className="text-navy">Telefone:</strong> {ind.telefone}</p>
                <p data-testid="perfil-cpf"><strong className="text-navy">CPF:</strong> {ind.cpf}</p>
                <p><strong className="text-navy">Cadastro:</strong> {ind.id_publico} · <span className="text-emerald-600 font-bold">{ind.status_label || "ATIVO"}</span></p>
              </div>
            </section>

            <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid="minha-foto-section">
              <h2 className="text-xs font-bold tracking-wider uppercase text-slate-400 mb-3 flex items-center gap-1.5"><Camera className="w-3.5 h-3.5" /> Minha foto</h2>
              <div className="flex items-center gap-4">
                {ind.foto ? (
                  <img src={fotoUrl(ind.id_publico, fotoV)} alt="Minha foto de perfil" className="w-20 h-20 rounded-full object-cover border-2 border-[#F5B301]" data-testid="foto-perfil-img"
                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/logo.jpg"; e.currentTarget.classList.remove("object-cover"); e.currentTarget.classList.add("object-contain", "bg-white", "p-1.5"); }} />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-navy text-[#F5B301] flex items-center justify-center text-2xl font-extrabold" data-testid="foto-perfil-iniciais">{ind.nome?.[0]}</div>
                )}
                <div className="flex-1 space-y-2">
                  <button onClick={() => cameraRef.current?.click()} data-testid="foto-tirar-btn"
                    className="w-full py-2.5 rounded-xl bg-navy text-[#F5B301] text-[11px] font-extrabold flex items-center justify-center gap-1.5 active:scale-95 transition-transform">
                    <Camera className="w-3.5 h-3.5" /> TIRAR FOTO
                  </button>
                  <button onClick={() => galeriaRef.current?.click()} data-testid="foto-galeria-btn"
                    className="w-full py-2.5 rounded-xl border-2 border-navy text-navy text-[11px] font-extrabold flex items-center justify-center gap-1.5 active:scale-95 transition-transform">
                    <ImagePlus className="w-3.5 h-3.5" /> ESCOLHER DA GALERIA
                  </button>
                </div>
              </div>
              <p className="text-[10px] text-slate-400 mt-2">Para alterar, envie uma nova foto. A foto de perfil é obrigatória no Clube e também aparece para a administração.</p>
              <input ref={cameraRef} type="file" accept="image/*" capture="user" className="hidden" data-testid="foto-camera-input" onChange={escolherFoto} />
              <input ref={galeriaRef} type="file" accept="image/*" className="hidden" data-testid="foto-galeria-input" onChange={escolherFoto} />
            </section>

            <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid="indicador-link-section">
              <h2 className="text-xs font-bold tracking-wider uppercase text-slate-400 mb-2 flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> Meu link de indicação</h2>
              <p className="text-[11px] text-navy font-semibold break-all" data-testid="indicador-link">{link?.link || "..."}</p>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <button onClick={copiar} data-testid="copiar-link-btn" className="py-2.5 rounded-xl bg-navy text-white text-[11px] font-extrabold flex items-center justify-center gap-1.5 active:scale-95 transition-transform">
                  <Copy className="w-3.5 h-3.5" /> COPIAR LINK
                </button>
                <button onClick={compartilhar} data-testid="compartilhar-link-btn" className="py-2.5 rounded-xl bg-[#F5B301] text-navy text-[11px] font-extrabold flex items-center justify-center gap-1.5 active:scale-95 transition-transform">
                  <Share2 className="w-3.5 h-3.5" /> COMPARTILHAR LINK
                </button>
              </div>

              <h3 className="text-xs font-bold tracking-wider uppercase text-slate-400 mt-5 mb-1 flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> Meu código de indicação</h3>
              <p className="text-xl font-heading font-extrabold text-navy tracking-widest" data-testid="indicador-codigo">{link?.codigo || ind.id_publico}</p>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button onClick={copiarCodigo} data-testid="copiar-codigo-btn" className="py-2.5 rounded-xl bg-navy text-white text-[11px] font-extrabold flex items-center justify-center gap-1.5 active:scale-95 transition-transform">
                  <Copy className="w-3.5 h-3.5" /> COPIAR CÓDIGO
                </button>
                <button onClick={compartilharCodigo} data-testid="compartilhar-codigo-btn" className="py-2.5 rounded-xl bg-[#F5B301] text-navy text-[11px] font-extrabold flex items-center justify-center gap-1.5 active:scale-95 transition-transform">
                  <Share2 className="w-3.5 h-3.5" /> COMPARTILHAR CÓDIGO
                </button>
              </div>
              <p className="text-[10px] text-slate-400 mt-2">
                Quem entrar pelo seu link ou informar seu código no orçamento fica vinculado a você automaticamente.
              </p>
              <button onClick={ativarNotificacoes} data-testid="indicador-notif-btn" className="w-full mt-3 py-2.5 rounded-xl border-2 border-slate-200 text-navy text-[11px] font-extrabold flex items-center justify-center gap-1.5 active:scale-95 transition-transform">
                <Bell className="w-3.5 h-3.5" /> ATIVAR NOTIFICAÇÕES NESTE APARELHO
              </button>
            </section>
          </>)}

          {tab === "divulgacao" && (
            <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid="divulgacao-section">
              <h2 className="text-xs font-bold tracking-wider uppercase text-slate-400 mb-1 flex items-center gap-1.5"><Megaphone className="w-3.5 h-3.5" /> Imagens para Propaganda e Divulgação</h2>
              <p className="text-[10px] text-slate-400 mb-3">Escolha uma imagem publicada pela administração e compartilhe — seu link de indicação é incluído automaticamente.</p>
              {!divulgacao ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-[#F5B301] animate-spin" /></div>
              ) : (divulgacao.imagens || []).length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-6" data-testid="divulgacao-vazio">Nenhuma imagem de divulgação publicada no momento. Volte em breve!</p>
              ) : (
                <div className="grid grid-cols-2 gap-2" data-testid="divulgacao-galeria">
                  {divulgacao.imagens.map((img) => (
                    <button key={img.imagem_id} onClick={() => abrirShare(img)} data-testid={`divulgacao-img-${img.imagem_id}`}
                      className="bg-slate-50 rounded-2xl border border-slate-100 overflow-hidden text-left active:scale-[0.98] transition-transform">
                      <div className="h-32 bg-slate-100 flex items-center justify-center">
                        <img src={imgUrl(img.url)} alt={img.nome} className="max-h-32 w-full object-contain" loading="lazy" />
                      </div>
                      <div className="p-2.5">
                        <p className="text-[10px] font-bold text-navy truncate">{img.nome}</p>
                        <span className="mt-1 inline-flex items-center gap-1 text-[9px] font-extrabold text-[#B8860B]">
                          <Share2 className="w-3 h-3" /> TOCAR PARA COMPARTILHAR
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </section>
          )}

          {tab === "ranking" && (
            <section className="space-y-4" data-testid="ranking-section">
              {!ranking ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-[#F5B301] animate-spin" /></div>
              ) : (<>
                {(ranking.premio?.valor || 0) > 0 && (
                  <div className="rounded-2xl bg-gradient-to-br from-[#F5B301] to-amber-400 p-4 text-center shadow-md" data-testid="ranking-premio">
                    <Trophy className="w-7 h-7 text-navy mx-auto mb-1" />
                    <p className="text-[10px] font-extrabold text-navy/70 uppercase tracking-wide">Prêmio do mês · 1º lugar</p>
                    <p className="font-heading text-2xl font-extrabold text-navy" data-testid="ranking-premio-valor">{brl(ranking.premio.valor)}</p>
                    <p className="text-[10px] text-navy/80 mt-0.5">Quem fechar mais contratos em {ranking.premio.mes} leva o prêmio!</p>
                  </div>
                )}
                {(() => {
                  const lider = rankingVisao === "mes" ? ranking.lider_mes : ranking.lider_geral;
                  const meu = rankingVisao === "mes" ? ranking.meu_mes : ranking.meu_geral;
                  const lista = rankingVisao === "mes" ? (ranking.ranking_mes || []) : (ranking.ranking_geral || []);
                  return (<>
                    {lider && (
                      <div className="bg-navy rounded-2xl p-4 flex items-center gap-3" data-testid="ranking-lider">
                        {lider.foto ? (
                          <img src={fotoUrl(lider.id_publico)} alt={`Foto de ${lider.nome}`} className="w-14 h-14 rounded-full object-cover border-2 border-[#F5B301]" data-testid="ranking-lider-foto"
                            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/logo.jpg"; e.currentTarget.classList.remove("object-cover"); e.currentTarget.classList.add("object-contain", "bg-white", "p-1.5"); }} />
                        ) : (
                          <div className="w-14 h-14 rounded-full bg-[#F5B301] flex items-center justify-center text-navy text-xl font-extrabold">{lider.nome?.[0]}</div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-[9px] font-extrabold text-[#F5B301] uppercase tracking-wide">🥇 {rankingVisao === "mes" ? "Líder do mês" : "Líder geral"}</p>
                          <p className="text-white font-heading font-bold text-base truncate" data-testid="ranking-lider-nome">{lider.nome}</p>
                          <p className="text-[10px] text-slate-300">{lider.contratos_fechados} contrato(s) fechado(s) · {lider.total_indicacoes} indicação(ões)</p>
                        </div>
                      </div>
                    )}
                    {meu && (
                      <p className="text-center text-[11px] font-bold text-navy bg-gold-light/60 border border-[#F5B301]/50 rounded-xl py-2" data-testid="ranking-minha-posicao">
                        Sua posição: {meu.posicao}º lugar · {meu.contratos_fechados} contrato(s) fechado(s)
                      </p>
                    )}
                    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
                      <div className="grid grid-cols-2 gap-1.5 mb-3">
                        <button onClick={() => setRankingVisao("mes")} data-testid="ranking-toggle-mes"
                          className={`py-2 rounded-xl text-[10px] font-extrabold active:scale-95 transition-transform ${rankingVisao === "mes" ? "bg-navy text-[#F5B301]" : "bg-slate-100 text-slate-500"}`}>
                          MÊS ATUAL
                        </button>
                        <button onClick={() => setRankingVisao("geral")} data-testid="ranking-toggle-geral"
                          className={`py-2 rounded-xl text-[10px] font-extrabold active:scale-95 transition-transform ${rankingVisao === "geral" ? "bg-navy text-[#F5B301]" : "bg-slate-100 text-slate-500"}`}>
                          GERAL
                        </button>
                      </div>
                      {lista.length === 0 && <p className="text-xs text-slate-400 text-center py-4" data-testid="ranking-vazio">Nenhum indicador no ranking ainda.</p>}
                      <div className="space-y-2" data-testid="ranking-list">
                        {lista.map((r) => (
                          <div key={r.indicador_id} className={`flex items-center gap-2.5 rounded-xl border p-2.5 ${r.indicador_id === ind.indicador_id ? "border-[#F5B301] bg-gold-light/50" : "border-slate-100 bg-slate-50"}`} data-testid={`ranking-row-${r.id_publico}`}>
                            <span className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-extrabold shrink-0 ${r.posicao === 1 ? "bg-[#F5B301] text-navy" : r.posicao <= 3 ? "bg-navy text-[#F5B301]" : "bg-slate-200 text-slate-600"}`}>{r.posicao}º</span>
                            {r.foto ? (
                              <img src={fotoUrl(r.id_publico)} alt="" className="w-9 h-9 rounded-full object-cover border border-slate-200"
                                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/logo.jpg"; e.currentTarget.classList.remove("object-cover"); e.currentTarget.classList.add("object-contain", "bg-white", "p-1"); }} />
                            ) : (
                              <div className="w-9 h-9 rounded-full bg-navy text-[#F5B301] flex items-center justify-center text-xs font-extrabold">{r.nome?.[0]}</div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-bold text-navy truncate">{r.nome}{r.indicador_id === ind.indicador_id ? " (você)" : ""}</p>
                              <p className="text-[9px] text-slate-400">{r.total_indicacoes} indicação(ões)</p>
                            </div>
                            <div className="text-right shrink-0">
                              <p className="text-sm font-heading font-extrabold text-navy">{r.contratos_fechados}</p>
                              <p className="text-[8px] text-slate-400 uppercase">fechados</p>
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-[9px] text-slate-400 mt-3">Contrato fechado = serviço concluído e recebimento confirmado. Desempate: mais indicações no período. O ranking atualiza automaticamente a cada contrato confirmado.</p>
                    </div>
                  </>);
                })()}
              </>)}
            </section>
          )}

          {tab === "carteira" && (<>
            <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid="carteira-resumo">
              <h2 className="text-xs font-bold tracking-wider uppercase text-slate-400 mb-3">Minha carteira</h2>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { l: "Clientes indicados", v: resumo.clientes_indicados ?? 0, tid: "resumo-indicados" },
                  { l: "Orçamentos em andamento", v: resumo.orcamentos_andamento ?? 0, tid: "resumo-orcamentos" },
                  { l: "Serviços contratados", v: resumo.servicos_contratados ?? 0, tid: "resumo-contratados" },
                  { l: "Aguardando conclusão", v: resumo.aguardando_conclusao ?? 0, tid: "resumo-aguardando" },
                  { l: "Comissões previstas", v: brl(resumo.comissoes_previstas ?? 0), tid: "resumo-previstas" },
                  { l: "Liberadas p/ pagamento", v: brl(resumo.comissoes_liberadas ?? 0), tid: "resumo-liberadas", destaque: true },
                  { l: "Pagamentos pendentes", v: resumo.pagamentos_pendentes ?? 0, tid: "resumo-pendentes" },
                  { l: "Comissões pagas", v: brl(resumo.comissoes_pagas ?? 0), tid: "resumo-pagas", pago: true },
                  { l: "Prêmios a receber", v: brl(resumo.premios_a_receber ?? 0), tid: "resumo-premios", destaque: true },
                ].map((c) => (
                  <div key={c.tid} data-testid={c.tid} className={`rounded-xl p-3 text-center border ${c.destaque ? "bg-blue-50 border-blue-100" : c.pago ? "bg-emerald-50 border-emerald-100" : "bg-slate-50 border-slate-100"}`}>
                    <p className="text-lg font-heading font-extrabold text-navy">{c.v}</p>
                    <p className="text-[9px] font-semibold text-slate-500 uppercase tracking-wide leading-tight">{c.l}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid="carteira-historico">
              <h2 className="text-xs font-bold tracking-wider uppercase text-slate-400 mb-2">Histórico de indicações</h2>
              {(carteira?.historico || []).length === 0 && (
                <p className="text-xs text-slate-400" data-testid="historico-vazio">Nenhuma indicação ainda. Compartilhe seu link!</p>
              )}
              <div className="space-y-2">
                {(carteira?.historico || []).map((h) => (
                  <div key={h.indicacao_id} className="rounded-xl border border-slate-100 bg-slate-50 p-3" data-testid={`historico-${h.indicacao_id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-bold text-navy truncate">{h.cliente_nome}</p>
                      <span className="text-[9px] font-extrabold px-2 py-0.5 rounded-full bg-navy text-[#F5B301] whitespace-nowrap">{h.status_indicacao_label}</span>
                    </div>
                    <p className="text-[10px] text-slate-500 mt-0.5">{h.tipo === "frete" ? "FRETE" : h.tipo === "mudanca" ? "MUDANÇA" : "—"} · indicado em {fmtDt(h.data_indicacao)}</p>
                    {h.comissao && (
                      <div className="mt-1.5 flex items-center justify-between">
                        <p className="text-[11px] text-slate-600">Comissão: <strong className="text-navy">{brl(h.comissao.valor)}</strong></p>
                        <span className={`text-[9px] font-extrabold px-2 py-0.5 rounded-full ${COM_STYLE[h.comissao.status] || "bg-slate-100 text-slate-500"}`} data-testid={`comissao-status-${h.comissao.comissao_id}`}>
                          {h.comissao.status_label}
                        </span>
                      </div>
                    )}
                    {h.comissao?.status === "liberada" && h.comissao.prazo_pagamento && (
                      <p className="text-[10px] text-blue-600 mt-1">Previsão de pagamento: até {fmtD(h.comissao.prazo_pagamento)}</p>
                    )}
                    {h.comissao?.status === "paga" && h.comissao.paga_em && (
                      <p className="text-[10px] text-emerald-600 mt-1">Pago em {fmtDt(h.comissao.paga_em)}</p>
                    )}
                  </div>
                ))}
              </div>
            </section>

            <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid="pix-section">
              <h2 className="text-xs font-bold tracking-wider uppercase text-slate-400 mb-2 flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> Dados para recebimento</h2>
              <span className={`inline-block text-[9px] font-extrabold px-2 py-1 rounded-full mb-3 ${PIX_STATUS_STYLE[ind.pix?.status || "nao_cadastrados"]}`} data-testid="pix-status-chip">
                {ind.pix?.status_label || "NÃO CADASTRADOS"}
              </span>
              {ind.pix?.status === "necessita_correcao" && ind.pix?.motivo_correcao && (
                <p className="text-[10px] text-red-500 mb-2" data-testid="pix-motivo-correcao">Correção solicitada: {ind.pix.motivo_correcao}</p>
              )}
              <form onSubmit={salvarPix} className="space-y-2.5">
                <select data-testid="pix-tipo" className={inputCls} value={pixForm.tipo} onChange={(e) => setPixForm((p) => ({ ...p, tipo: e.target.value }))}>
                  {PIX_TIPOS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                </select>
                <input data-testid="pix-chave" className={inputCls} placeholder="Chave Pix" value={pixForm.chave} onChange={(e) => setPixForm((p) => ({ ...p, chave: e.target.value }))} />
                <input data-testid="pix-titular" className={inputCls} placeholder="Nome do titular" value={pixForm.titular_nome} onChange={(e) => setPixForm((p) => ({ ...p, titular_nome: e.target.value }))} />
                <input data-testid="pix-doc" inputMode="numeric" className={inputCls} placeholder="CPF/CNPJ do titular (somente números)" value={pixForm.titular_doc} onChange={(e) => setPixForm((p) => ({ ...p, titular_doc: e.target.value }))} />
                <label className="flex items-start gap-2 text-[11px] text-slate-600">
                  <input type="checkbox" data-testid="pix-confirmacao" className="mt-0.5 accent-[#F5B301]" checked={pixForm.confirmacao} onChange={(e) => setPixForm((p) => ({ ...p, confirmacao: e.target.checked }))} />
                  Confirmo que os dados de recebimento estão corretos e são de minha titularidade.
                </label>
                <button type="submit" data-testid="pix-salvar-btn" disabled={pixSalvando} className="w-full py-3 rounded-xl bg-navy text-white text-xs font-extrabold active:scale-95 transition-transform disabled:opacity-50">
                  {pixSalvando ? "SALVANDO..." : "SALVAR DADOS PIX"}
                </button>
              </form>
            </section>

            {(carteira?.premiacoes || []).length > 0 && (
            <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid="premiacoes-section">
              <h2 className="text-xs font-bold tracking-wider uppercase text-slate-400 mb-2 flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5" /> Minhas premiações</h2>
              {carteira.premiacoes.map((p) => (
                <div key={p.premio_id} className="rounded-xl border border-slate-100 bg-slate-50 p-3 mb-2" data-testid={`premiacao-${p.premio_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[11px] font-bold text-navy">🏆 1º lugar · Ranking {p.mes_referencia} · {brl(p.valor)}</p>
                    <span className={`text-[9px] font-extrabold px-2 py-0.5 rounded-full ${COM_STYLE[p.status] || "bg-slate-100 text-slate-500"}`} data-testid={`premiacao-status-${p.premio_id}`}>
                      {p.status_label}
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-500 mt-0.5">
                    Mês de referência: {p.mes_referencia} · {p.contratos_fechados} contrato(s) fechado(s)
                    {p.paga_em ? ` · pago em ${fmtDt(p.paga_em)}` : ""}
                  </p>
                </div>
              ))}
            </section>
            )}
          </>)}

          {tab === "suporte" && (
            <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid="suporte-section">
              <h2 className="text-xs font-bold tracking-wider uppercase text-slate-400 mb-2 flex items-center gap-1.5"><MessageCircle className="w-3.5 h-3.5" /> CONTACTAR SUPORTE</h2>
              <div className="max-h-64 overflow-y-auto space-y-2 mb-3" data-testid="suporte-msgs">
                {msgs.length === 0 && <p className="text-xs text-slate-400">Envie sua dúvida para a administração. Respondemos por aqui mesmo.</p>}
                {msgs.map((m) => (
                  <div key={m.msg_id} className={`max-w-[85%] rounded-2xl px-3 py-2 text-xs ${m.remetente === "indicador" ? "ml-auto bg-navy text-white" : "bg-gold-light/60 text-navy"}`} data-testid={`msg-${m.msg_id}`}>
                    <p className="whitespace-pre-wrap">{m.texto}</p>
                    <p className={`text-[9px] mt-1 ${m.remetente === "indicador" ? "text-slate-300" : "text-slate-500"}`}>
                      {m.remetente === "indicador" ? "Você" : "Administração"} · {fmtDt(m.em)}
                    </p>
                  </div>
                ))}
              </div>
              <form onSubmit={enviarMsg} className="flex gap-2">
                <input data-testid="suporte-msg-input" className={`${inputCls} flex-1`} placeholder="Escreva sua mensagem..." value={msgTexto} onChange={(e) => setMsgTexto(e.target.value)} />
                <button type="submit" data-testid="suporte-enviar-btn" disabled={enviando} className="px-4 rounded-xl bg-[#F5B301] text-navy active:scale-95 transition-transform disabled:opacity-50" aria-label="Enviar mensagem">
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </section>
          )}

          <p className="text-center"><Link to="/" className="text-[11px] text-slate-400 underline" data-testid="clube-painel-home">Voltar ao início</Link></p>
        </div>

        {fotoPendente && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center" data-testid="foto-preview-modal" onClick={cancelarFoto}>
            <div className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-5" onClick={(e) => e.stopPropagation()}>
              <h3 className="font-heading font-bold text-navy text-sm mb-3">Confirmar nova foto de perfil</h3>
              <div className="flex justify-center">
                <img src={fotoPendente.preview} alt="Prévia da nova foto" className="w-44 h-44 rounded-full object-cover border-4 border-[#F5B301]" data-testid="foto-preview-img" />
              </div>
              <div className="grid grid-cols-2 gap-2 mt-4">
                <button onClick={cancelarFoto} data-testid="foto-cancelar-btn"
                  className="py-3 rounded-xl border-2 border-slate-200 text-slate-500 text-xs font-extrabold active:scale-95 transition-transform">
                  CANCELAR
                </button>
                <button onClick={confirmarFoto} disabled={fotoSalvando} data-testid="foto-confirmar-btn"
                  className="py-3 rounded-xl bg-navy text-[#F5B301] text-xs font-extrabold flex items-center justify-center gap-1.5 active:scale-95 transition-transform disabled:opacity-50">
                  {fotoSalvando && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  {fotoSalvando ? "SALVANDO..." : "CONFIRMAR E SALVAR"}
                </button>
              </div>
            </div>
          </div>
        )}

        {shareImg && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center" data-testid="share-modal" onClick={() => setShareImg(null)}>
            <div className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-bold text-navy text-sm">Compartilhar divulgação</h3>
                <button onClick={() => setShareImg(null)} data-testid="share-fechar-btn" className="text-slate-400 hover:text-slate-600"><XCircle className="w-5 h-5" /></button>
              </div>
              <div className="bg-slate-100 rounded-2xl p-2 flex items-center justify-center">
                <img src={imgUrl(shareImg.url)} alt={shareImg.nome} className="max-h-52 object-contain" data-testid="share-preview-img" />
              </div>
              <p className="text-[10px] font-bold text-navy mt-2 truncate">{shareImg.nome}</p>
              <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wide mt-3 mb-1">Mensagem (você pode ajustar o texto)</label>
              <textarea data-testid="share-texto-input" rows={4} maxLength={600} className={inputCls} value={shareTexto} onChange={(e) => setShareTexto(e.target.value)} />
              <div className="rounded-xl bg-gold-light/60 border border-[#F5B301]/50 p-3 mt-2">
                <p className="text-[10px] font-bold text-navy uppercase">Seu link de indicação (incluído automaticamente)</p>
                <p className="text-[11px] text-navy font-semibold break-all" data-testid="share-link">{linkIndicacao || "carregando..."}</p>
              </div>
              <button onClick={compartilharImagem} disabled={shareBusy} data-testid="share-confirm-btn"
                className="mt-3 w-full py-3.5 rounded-xl bg-[#F5B301] text-navy text-xs font-extrabold flex items-center justify-center gap-1.5 active:scale-95 transition-transform disabled:opacity-50">
                {shareBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                {shareBusy ? "PREPARANDO..." : "COMPARTILHAR"}
              </button>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button onClick={copiarTextoLink} data-testid="share-copiar-btn"
                  className="py-2.5 rounded-xl bg-navy text-white text-[10px] font-extrabold flex items-center justify-center gap-1.5 active:scale-95 transition-transform">
                  <Copy className="w-3.5 h-3.5" /> COPIAR TEXTO + LINK
                </button>
                <button onClick={baixarImagem} data-testid="share-baixar-btn"
                  className="py-2.5 rounded-xl border-2 border-navy text-navy text-[10px] font-extrabold flex items-center justify-center gap-1.5 active:scale-95 transition-transform">
                  <Download className="w-3.5 h-3.5" /> BAIXAR IMAGEM
                </button>
              </div>
              <p className="text-[10px] text-slate-400 mt-3 leading-snug">
                No WhatsApp a imagem e o texto com seu link costumam ir juntos. Em Instagram, Facebook e Stories, alguns formatos não aceitam link clicável junto com a imagem — nesse caso, copie o texto com o link e cole na legenda ou na publicação.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
